import { CogIcon } from "lucide-react";

import FormatSelector from "./FormatSelector";
import MetadataSection from "./MetadataSection";
import SettingsSection from "./SettingsSection";

import { TemplateFormat } from "@/components/Playground/types";
import { Switch } from "@/components/switch";
import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copy-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { Metadata, PromptBlueprint } from "@/types";

const STREAMING_REQUIRED_TOOLTIP = "The selected model only supports streaming responses";
const STREAMING_DISALLOWED_TOOLTIP =
  "The selected model does not return images when streaming is enabled";

function getStreamingConstraintTooltip(
  streamingRequiredByModel: boolean,
  streamingDisallowedByModel: boolean,
): string | undefined {
  if (streamingRequiredByModel) {
    return STREAMING_REQUIRED_TOOLTIP;
  }
  if (streamingDisallowedByModel) {
    return STREAMING_DISALLOWED_TOOLTIP;
  }
  return undefined;
}

function getStreamingConstraintHelperText(
  streamingRequiredByModel: boolean,
  streamingDisallowedByModel: boolean,
): string | undefined {
  if (streamingRequiredByModel) {
    return "The selected model only supports streaming responses, so this can't be turned off.";
  }
  if (streamingDisallowedByModel) {
    return "The selected model does not return images when streaming is enabled, so this can't be turned on.";
  }
  return undefined;
}

interface StreamingToggleProps {
  readonly: boolean;
  enableStreaming: boolean;
  onToggleStreaming?: () => void;
  streamingRequiredByModel: boolean;
  streamingDisallowedByModel: boolean;
}

const StreamingToggle = ({
  readonly,
  enableStreaming,
  onToggleStreaming,
  streamingRequiredByModel,
  streamingDisallowedByModel,
}: StreamingToggleProps) => {
  const isLocked = streamingRequiredByModel || streamingDisallowedByModel;
  const tooltipContent = getStreamingConstraintTooltip(
    streamingRequiredByModel,
    streamingDisallowedByModel,
  );
  const helperText = getStreamingConstraintHelperText(
    streamingRequiredByModel,
    streamingDisallowedByModel,
  );

  return (
    <>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600">Stream responses in playground</span>
        <BasicTooltip content={tooltipContent ?? ""} shouldShow={isLocked}>
          <Switch
            checked={enableStreaming}
            onChange={onToggleStreaming}
            disabled={readonly || isLocked}
          />
        </BasicTooltip>
      </div>
      {helperText && <p className="mt-1.5 text-xs text-gray-500">{helperText}</p>}
    </>
  );
};

interface MessagesSettingsProps {
  readonly?: boolean;
  formats: string[];
  currentFormat: string;
  onFormatChange?: (format: TemplateFormat) => void;
  tooltipContent?: string;
  dialogTitle?: string;
  dialogDescription?: string;
  formatSectionTitle?: string;
  metadata?: Metadata;
  setMetadata?: (metadata: Metadata) => void;
  enableMarkdown?: boolean;
  onToggleMarkdown?: () => void;
  enableStreaming?: boolean;
  onToggleStreaming?: () => void;
  streamingRequiredByModel?: boolean;
  streamingDisallowedByModel?: boolean;
  blueprint?: PromptBlueprint;
  isXRayMode?: boolean;
  onToggleXRay?: () => void;
  hasXRay?: boolean;
  isXRayLoading?: boolean;
  showStreaming?: boolean;
  showMetadata?: boolean;
  showExport?: boolean;
  snippetText?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}

const MessagesSettings = ({
  readonly = false,
  formats,
  currentFormat,
  onFormatChange,
  tooltipContent = "Prompt Settings",
  dialogTitle = "Settings",
  dialogDescription = "Customize your template preferences",
  formatSectionTitle = "Message Format",
  metadata = {},
  setMetadata,
  enableMarkdown = false,
  onToggleMarkdown,
  enableStreaming = true,
  onToggleStreaming,
  streamingRequiredByModel = false,
  streamingDisallowedByModel = false,
  blueprint,
  isXRayMode = false,
  onToggleXRay,
  hasXRay = false,
  isXRayLoading = false,
  showStreaming = true,
  showMetadata = true,
  showExport = true,
  snippetText,
  open,
  onOpenChange,
  hideTrigger = false,
}: MessagesSettingsProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    {!hideTrigger && (
      <BasicTooltip content={tooltipContent}>
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full text-gray-500 transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 active:scale-95"
          >
            <CogIcon className="h-4 w-4" />
          </Button>
        </DialogTrigger>
      </BasicTooltip>
    )}
    <DialogContent className="w-full max-w-lg overflow-hidden">
      <DialogHeader>
        <DialogTitle className="text-xl font-semibold">{dialogTitle}</DialogTitle>
        <DialogDescription className="text-sm text-gray-500">{dialogDescription}</DialogDescription>
      </DialogHeader>
      <div className="w-full space-y-6 overflow-y-auto px-6 pb-2">
        <SettingsSection
          title={formatSectionTitle}
          info="Modify template variable parsing behavior"
        >
          <FormatSelector
            formats={formats}
            currentFormat={currentFormat}
            onChange={onFormatChange}
            readonly={readonly}
          />
        </SettingsSection>

        <div className="mx-4 border-t border-gray-200" />

        <SettingsSection
          title="Markdown Rendering"
          info="Enable markdown rendering for message content"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Render markdown in messages</span>
            <Switch checked={enableMarkdown} onChange={onToggleMarkdown} disabled={readonly} />
          </div>
        </SettingsSection>

        {showStreaming && (
          <>
            <div className="mx-4 border-t border-gray-200" />

            <SettingsSection
              title="Streaming"
              info="Stream responses token by token in the playground"
            >
              <StreamingToggle
                readonly={readonly}
                enableStreaming={enableStreaming}
                onToggleStreaming={onToggleStreaming}
                streamingRequiredByModel={streamingRequiredByModel}
                streamingDisallowedByModel={streamingDisallowedByModel}
              />
            </SettingsSection>
          </>
        )}

        {hasXRay && !readonly && (
          <>
            <div className="mx-4 border-t border-gray-200" />

            <SettingsSection title="X-Ray Mode">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    Expand all snippets and prompt widgets
                  </span>
                </div>
                <Switch checked={isXRayMode} onChange={onToggleXRay} disabled={isXRayLoading} />
              </div>
            </SettingsSection>
          </>
        )}

        {showMetadata && (
          <>
            <div className="mx-4 border-t border-gray-200" />

            <SettingsSection
              title="Metadata"
              info="Use metadata to store additional information with your prompt template"
            >
              <MetadataSection
                metadata={metadata}
                setMetadata={readonly ? undefined : setMetadata}
                readonly={readonly}
              />
            </SettingsSection>
          </>
        )}

        {(showExport || snippetText !== undefined) && (
          <>
            <div className="mx-4 border-t border-gray-200" />

            <SettingsSection title="Export" info="Copy your prompt configuration to clipboard">
              <div className="flex items-center justify-center gap-2">
                <span className="text-sm text-gray-600">Copy to Clipboard</span>
                <CopyButton
                  text={snippetText ?? JSON.stringify(blueprint, null, 2)}
                  variant="outline"
                />
              </div>
            </SettingsSection>
          </>
        )}
      </div>
    </DialogContent>
  </Dialog>
);

export default MessagesSettings;
